import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ChevronLeft, Plus, Trash2 } from 'lucide-react';
import { describeGoal, GOAL_METRICS, STORE_LAYOUT_LABELS, STORE_LAYOUTS, type GoalMetric, type StoreLayout } from '@bp/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { Goal, Store, TemplateDetail, TemplateSummary, Worker } from '@/lib/types';
import { Badge, Button, Card, CardHeader, Dialog, Empty, ErrorNote, Field, Input, Loading, PageHeader, Select, Tabs } from '@/components/ui';

export function StoreSetupListPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['stores', 'all'], queryFn: () => api.get<Store[]>('/api/stores?include_inactive=1') });
  const [creating, setCreating] = useState(false);
  return (
    <>
      <PageHeader title="Store setup" subtitle="Store details, workers, who is accountable for each checklist point, and goals." actions={can('create_stores') && <Button onClick={() => setCreating(true)}><Plus className="size-4" />New store</Button>} />
      {q.isLoading ? <Loading /> : q.error ? <ErrorNote error={q.error} /> : q.data!.length === 0 ? <Card><Empty title="No stores yet." /></Card> : (
        <Card>
          <ul className="divide-y">
            {q.data!.map((s) => (
              <li key={s.id}>
                <Link to={`/admin/stores/${s.id}`} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-muted/50">
                  <div><p className="font-medium">{s.name} <span className="text-sm font-normal text-muted-fg">#{s.store_number}</span></p><p className="text-xs text-muted-fg">{[s.location, s.region, STORE_LAYOUT_LABELS[s.layout]].filter(Boolean).join(' · ')}</p></div>
                  {!s.active && <Badge tone="warn">Inactive</Badge>}
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}
      {creating && <StoreDialog onClose={() => setCreating(false)} onSaved={() => qc.invalidateQueries({ queryKey: ['stores'] })} />}
    </>
  );
}

function StoreDialog({ store, onClose, onSaved }: { store?: Store; onClose: () => void; onSaved: () => void }) {
  const { can } = useAuth();
  const [f, setF] = useState({
    store_number: store?.store_number ?? '', name: store?.name ?? '', location: store?.location ?? '', region: store?.region ?? '',
    layout: (store?.layout ?? 'standard') as StoreLayout, kitchens: (store?.kitchens ?? ['Main']).join(', '),
    notification_emails: (store?.notification_emails ?? []).join(', '), active: store?.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const list = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);
  const creator = can('create_stores');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={store ? 'Edit store' : 'New store'} footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={async () => {
      setBusy(true);
      setError(null);
      try {
        const body = { ...f, kitchens: list(f.kitchens).length ? list(f.kitchens) : ['Main'], notification_emails: list(f.notification_emails) };
        if (store) await api.patch(`/api/stores/${store.id}`, body); else await api.post('/api/stores', body);
        toast.success('Saved.');
        onSaved();
        onClose();
      } catch (e) { setError(e); } finally { setBusy(false); }
    }}>Save</Button></>}>
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Store #"><Input value={f.store_number} disabled={!!store && !creator} onChange={(e) => setF({ ...f, store_number: e.target.value })} /></Field>
          <Field label="Name" className="sm:col-span-2"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Location"><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} /></Field>
          <Field label="Region"><Input value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} /></Field>
        </div>
        <Field label="Layout" hint="Templates can be limited to a layout, e.g. the Carhop checklist to car-service stores.">
          <Select value={f.layout} onChange={(e) => setF({ ...f, layout: e.target.value as StoreLayout })}>{STORE_LAYOUTS.map((l) => <option key={l} value={l}>{STORE_LAYOUT_LABELS[l]}</option>)}</Select>
        </Field>
        <Field label="Kitchens" hint="Comma-separated. Used by Health Dept logging later."><Input value={f.kitchens} onChange={(e) => setF({ ...f, kitchens: e.target.value })} /></Field>
        <Field label="Notification emails" hint="Comma-separated."><Input value={f.notification_emails} onChange={(e) => setF({ ...f, notification_emails: e.target.value })} /></Field>
        {store && creator && <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="size-4" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />Active</label>}
        <ErrorNote error={error} />
      </div>
    </Dialog>
  );
}

type Tab = 'workers' | 'points' | 'goals';

export function StoreSetupPage() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) ?? 'workers';
  const qc = useQueryClient();
  const store = useQuery({ queryKey: ['store', id], queryFn: () => api.get<Store>(`/api/stores/${id}`) });
  const [editing, setEditing] = useState(false);
  if (store.isLoading) return <Loading />;
  if (store.error) return <ErrorNote error={store.error} />;
  const s = store.data!;
  return (
    <>
      <PageHeader
        back={<Link to="/admin/stores" className="mb-2 inline-flex items-center gap-1 text-sm text-muted-fg hover:text-fg"><ChevronLeft className="size-4" />Store setup</Link>}
        title={s.name}
        subtitle={<>#{s.store_number} · {STORE_LAYOUT_LABELS[s.layout]}{s.region && ` · ${s.region}`}</>}
        actions={<Button variant="outline" onClick={() => setEditing(true)}>Edit details</Button>}
      />
      <Tabs<Tab> className="mb-4 w-fit" value={tab} onChange={(v) => setParams({ tab: v }, { replace: true })} tabs={[{ value: 'workers', label: 'Workers' }, { value: 'points', label: 'Point assignments' }, { value: 'goals', label: 'Goals' }]} />
      {tab === 'workers' && <WorkersTab storeId={s.id} />}
      {tab === 'points' && <PointsTab storeId={s.id} />}
      {tab === 'goals' && <GoalsTab storeId={s.id} />}
      {editing && <StoreDialog store={s} onClose={() => setEditing(false)} onSaved={() => { void qc.invalidateQueries({ queryKey: ['store', id] }); void qc.invalidateQueries({ queryKey: ['stores'] }); }} />}
    </>
  );
}

function WorkersTab({ storeId }: { storeId: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['workers', storeId, 'all'], queryFn: () => api.get<Worker[]>(`/api/stores/${storeId}/workers?include_inactive=1`) });
  const [name, setName] = useState('');
  const [position, setPosition] = useState('');
  const refresh = () => qc.invalidateQueries({ queryKey: ['workers', storeId] });
  return (
    <Card className="max-w-3xl">
      <CardHeader title="Store workers" subtitle="People accountable for checklist points and assignable to action items. They don't need a login." />
      <form className="flex flex-wrap gap-2 border-b p-4" onSubmit={async (e) => {
        e.preventDefault();
        try { await api.post(`/api/stores/${storeId}/workers`, { name, position }); setName(''); setPosition(''); void refresh(); } catch (err) { toast.error((err as Error).message); }
      }}>
        <Input className="min-w-40 flex-1" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <Input className="w-44" placeholder="Position (e.g. Shift Lead)" value={position} onChange={(e) => setPosition(e.target.value)} />
        <Button type="submit"><Plus className="size-4" />Add</Button>
      </form>
      {q.isLoading ? <Loading /> : (q.data?.length ?? 0) === 0 ? <Empty title="No workers yet." /> : (
        <ul className="divide-y">
          {q.data!.map((w) => (
            <li key={w.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
              <span className={w.active ? '' : 'text-muted-fg line-through'}>{w.name}{w.position && <span className="text-muted-fg"> · {w.position}</span>}</span>
              <Button size="sm" variant="ghost" onClick={async () => { await api.patch(`/api/workers/${w.id}`, { active: !w.active }); void refresh(); }}>{w.active ? 'Deactivate' : 'Reactivate'}</Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function PointsTab({ storeId }: { storeId: string }) {
  const templates = useQuery({ queryKey: ['templates', 'usable', storeId], queryFn: () => api.get<TemplateSummary[]>(`/api/templates/usable?store_id=${storeId}`) });
  const [templateId, setTemplateId] = useState('');
  const tid = templateId || templates.data?.[0]?.id || '';
  const tpl = useQuery({ queryKey: ['template', tid], queryFn: () => api.get<TemplateDetail>(`/api/templates/${tid}`), enabled: !!tid });
  const workers = useQuery({ queryKey: ['workers', storeId], queryFn: () => api.get<Worker[]>(`/api/stores/${storeId}/workers`) });
  const assigned = useQuery({ queryKey: ['points', storeId, tid], queryFn: () => api.get<{ item_key: string; worker_id: string }[]>(`/api/stores/${storeId}/point-assignments?template_id=${tid}`), enabled: !!tid });
  const qc = useQueryClient();
  const byItem = new Map(assigned.data?.map((a) => [a.item_key, a.worker_id]));

  async function assign(item_key: string, worker_id: string | null) {
    try {
      await api.put(`/api/stores/${storeId}/point-assignments`, { template_id: tid, assignments: [{ item_key, worker_id }] });
      void qc.invalidateQueries({ queryKey: ['points', storeId, tid] });
    } catch (e) { toast.error((e as Error).message); }
  }

  if (templates.isLoading) return <Loading />;
  if (!tid) return <Card><Empty title="No published templates for this store." /></Card>;
  return (
    <Card>
      <CardHeader title="Who is accountable for each point" subtitle="Shown on the audit and used as the default owner of a failed item's task." action={
        <Select className="w-56" value={tid} onChange={(e) => setTemplateId(e.target.value)}>{templates.data!.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</Select>
      } />
      {(workers.data?.length ?? 0) === 0 && <p className="border-b bg-warn-bg px-4 py-2 text-sm text-warn">Add workers on the Workers tab first.</p>}
      {tpl.isLoading ? <Loading /> : tpl.data?.draft.sections.map((s) => (
        <div key={s.key}>
          <p className="bg-muted px-4 py-2 text-xs font-semibold tracking-wide text-muted-fg uppercase">{s.label}</p>
          <ul className="divide-y">
            {s.items.map((i) => (
              <li key={i.key} className="flex flex-wrap items-center justify-between gap-3 px-4 py-2 text-sm">
                <span className="min-w-0 flex-1">{i.text}</span>
                <Select className="h-9 w-52" value={byItem.get(i.key) ?? ''} onChange={(e) => assign(i.key, e.target.value || null)}>
                  <option value="">Nobody</option>
                  {workers.data?.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </Select>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </Card>
  );
}

function GoalsTab({ storeId }: { storeId: string }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const goals = useQuery({ queryKey: ['goals', storeId], queryFn: () => api.get<Goal[]>(`/api/stores/${storeId}/goals`) });
  const templates = useQuery({ queryKey: ['templates', 'usable', storeId], queryFn: () => api.get<TemplateSummary[]>(`/api/templates/usable?store_id=${storeId}`) });
  const def = templates.data?.[0];
  const tpl = useQuery({ queryKey: ['template', def?.id], queryFn: () => api.get<TemplateDetail>(`/api/templates/${def!.id}`), enabled: !!def && can('view_templates') });
  const sections = tpl.data?.draft.sections ?? [];
  const [metric, setMetric] = useState<GoalMetric>('overall_score');
  const [target, setTarget] = useState('90');
  const [section, setSection] = useState('');
  const refresh = () => qc.invalidateQueries({ queryKey: ['goals', storeId] });
  const labelFor = (k: string | null) => sections.find((s) => s.key === k)?.label;
  const metricLabel: Record<GoalMetric, string> = { overall_score: 'Overall score at least', section_score: 'Section score at least', max_failed_items: 'Failed items at most' };

  return (
    <Card className="max-w-3xl">
      <CardHeader title="Goals" subtitle="Shown live during audits and checked when an official audit is finished." />
      {can('manage_goals') && (
        <form className="flex flex-wrap items-end gap-2 border-b p-4" onSubmit={async (e) => {
          e.preventDefault();
          try {
            await api.post(`/api/stores/${storeId}/goals`, { metric, target: Number(target), section_key: metric === 'section_score' ? section || sections[0]?.key : null });
            void refresh();
          } catch (err) { toast.error((err as Error).message); }
        }}>
          <Field label="Goal"><Select className="w-56" value={metric} onChange={(e) => setMetric(e.target.value as GoalMetric)}>{GOAL_METRICS.map((m) => <option key={m} value={m}>{metricLabel[m]}</option>)}</Select></Field>
          {metric === 'section_score' && <Field label="Section"><Select className="w-56" value={section} onChange={(e) => setSection(e.target.value)}>{sections.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}</Select></Field>}
          <Field label={metric === 'max_failed_items' ? 'Items' : 'Percent'}><Input className="w-24" type="number" value={target} onChange={(e) => setTarget(e.target.value)} /></Field>
          <Button type="submit"><Plus className="size-4" />Add goal</Button>
        </form>
      )}
      {goals.isLoading ? <Loading /> : (goals.data?.length ?? 0) === 0 ? <Empty title="No goals yet." /> : (
        <ul className="divide-y">
          {goals.data!.map((g) => (
            <li key={g.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
              <span className={g.active ? '' : 'text-muted-fg line-through'}>{describeGoal(g, labelFor(g.section_key))}</span>
              {can('manage_goals') && (
                <span className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={async () => { await api.patch(`/api/goals/${g.id}`, { active: !g.active }); void refresh(); }}>{g.active ? 'Pause' : 'Resume'}</Button>
                  <Button size="sm" variant="ghost" className="text-fail" onClick={async () => { if (confirm('Delete this goal?')) { await api.del(`/api/goals/${g.id}`); void refresh(); } }}><Trash2 className="size-4" /></Button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
